import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, toDisplayString as _toDisplayString, createElementVNode as _createElementVNode, createTextVNode as _createTextVNode, withCtx as _withCtx, createVNode as _createVNode, createElementBlock as _createElementBlock, normalizeClass as _normalizeClass, resolveComponent as _resolveComponent, isRef as _isRef, Fragment as _Fragment } from "vue"

const _hoisted_1 = { class: "text-center text-xl" }
const _hoisted_2 = { class: "font-bold" }
const _hoisted_3 = {
  key: 0,
  class: "el-tag el-tag--success el-tag--medium fw-bold rounded-none"
}
const _hoisted_4 = { class: "fw-bolder whitespace-pre-line" }

import { useRoute, useRouter } from "vue-router";
import { computed, nextTick, onMounted, ref, watch } from "vue";
import {
  setBreadcrumbLinks,
  setCurrentPageTitle,
} from "@/core/helpers/breadcrumb";
import {
  IDinhMucCoDinhNewResponseBase,
  IVatTuDinhMucCoDinh,
  IVatTuResponseBase,
  IVatTuDinhMucCoDinhResponseBase,
} from "@/core/interfaces/ApiResponses";
import { swalErrNotification, swalNotification } from "@/core/helpers/utils";
import { useButtonCustom } from "@/core/hooks/use-button-custom";
import { useDateTime } from "@/core/hooks/use-date-time-hook";
import { useLoading } from "@/core/hooks/use-loading";
import { AddVatTuItemsFor } from "@/core/enums/addVatTuItemsFor.enum";
import { DinhMucCoDinhService } from "@/core/services/DinhMucCoDinh.service";
import AddVatTuItemV2 from "@/components/add-vat-tu-items-v2/AddVatTuItemV2.vue";
import ButtonCustom from "@/components/shared/buttons/ButtonCustom.vue";
import DinhMucCoDinhVatTuTable from "@/components/dinh-muc-co-dinh-section/dinh-muc-co-dinh-table/DinhMucCoDinhVatTuTable.vue";
import LabelCustom from "@/components/shared/label-custom/LabelCustom.vue";
import PageContentBase from "@/layout/_shared/PageContentBase.vue";
import PageContentSection from "@/layout/_shared/PageContentSection.vue";
import ResponsiveLayout from "@/layout/_shared/ResponsiveLayout.vue";
import ConfirmationDialog from "@/components/confirmation-dialog/ConfirmationDialog.vue";
import { usePagination } from "@/core/hooks/use-pagination";
import { VatTuService } from "@/core/services/VatTu.service";
import debounce from "lodash/debounce";
import Pagination from "@/components/shared/pagination/Pagination.vue";
import Can from "@/components/Can.vue";
import { DINH_MUC_CO_DINH } from "@/core/constants/permissions";
import moment from "moment/moment";
import FilterContainer from "@/layout/_shared/FilterContainer.vue";
import FilterItem from "@/layout/_shared/FilterItem.vue";
import BaseLink from "@/components/shared/base-link/BaseLink.vue";
import DetailTopicLottie from "@/components/shared/detail-topic-lottie/DetailTopicLottie.vue";
import { useStore } from "vuex";


export default /*#__PURE__*/_defineComponent({
  __name: 'Detail',
  setup(__props) {

const { formatDisplayingDate } = useDateTime();

const {
  params: { id: idDinhMuc },
} = useRoute();

const { push } = useRouter();

const store = useStore();

const dinhMucDetail = ref<IDinhMucCoDinhNewResponseBase | null>(null);

const { startLoading, endLoading } = useLoading();

const { ButtonsType, ButtonTypeColors } = useButtonCustom();

// Expired date detection
const isDinhMucHetHan = computed(() => {
  if (!dinhMucDetail.value?.ngay_het_han) {
    return false; // No expiry date means unlimited
  }

  const today = moment();
  const expiryDate = moment(dinhMucDetail.value.ngay_het_han);

  return today.isAfter(expiryDate, "day");
});

// Show edit button condition (hidden when expired)
const showEditButton = computed(() => {
  return !!dinhMucDetail.value && !isDinhMucHetHan.value;
});

const vatTuItems = ref<IVatTuDinhMucCoDinh[]>([]);

const {
  currentPage,
  perPage,
  totalItems,
  searchTerm,
  loading: loadingFetchingVatTu,
} = usePagination();

const vatTuDinhMucTransform = computed<IVatTuResponseBase[]>(() => {
  if (!dinhMucDetail.value || !dinhMucDetail.value.vat_tu) return [];

  return dinhMucDetail.value.vat_tu.map((item) => ({
    ...item,
    so_luong: item.pivot.so_luong,
  }));
});

const isAddVatTuModalDisplay = ref(false);

const fetchData = async () => {
  try {
    await startLoading();

    const {
      data: { data },
    } = await DinhMucCoDinhService.get(+idDinhMuc);

    document.title = `${data.danh_muc_toa_xe.ten_danh_muc} (${data.cap_sua_chua.ten_cap_sc}) - Định mức cố định - Đường sắt Hà Nội`;

    dinhMucDetail.value = data;

    await endLoading();
  } catch (error) {
    await endLoading();

    await swalErrNotification(error, "Có lỗi xảy ra");
  }
};

const onDeleteButtonClick = () => {
  if (!dinhMucDetail.value) {
    return;
  }

  store.commit("ConfirmationDialogModule/setDialog", {
    message: `Bạn có chắc chắn muốn xoá định mức cố định ID ${dinhMucDetail.value.id}?`,
    onConfirm: async () => {
      store.commit("ConfirmationDialogModule/closeDialog");
      await startLoading();

      try {
        await DinhMucCoDinhService.delete(
          dinhMucDetail.value?.id ?? +idDinhMuc
        );
        await endLoading();
        await swalNotification("Xoá định mức cố định thành công");

        await push({
          name: "danh-sach-dinh-muc-co-dinh",
        });
      } catch (e) {
        await endLoading();
        await swalErrNotification(e, "Không thể xoá định mức cố định này");
      }
    },
  });
};

const fetchVatTuDinhMuc = async () => {
  if (!dinhMucDetail.value) return;

  loadingFetchingVatTu.value = true;

  try {
    const {
      data: {
        data: { data, total },
      },
    } = await VatTuService.getVatTuItemsByDinhMucCoDinhId({
      id_dinh_muc: dinhMucDetail.value?.id ?? 0,
      current_page: currentPage.value,
      per_page: perPage.value,
      s: searchTerm.value,
    });

    loadingFetchingVatTu.value = false;

    vatTuItems.value = data.map((item: IVatTuDinhMucCoDinhResponseBase) => ({
      ...item,
      pivot: {
        id_vat_tu: item.id,
        id_dinh_muc: dinhMucDetail.value?.id ?? 0,
        so_luong: item.so_luong_dinh_muc,
      },
    }));

    totalItems.value = total;
  } catch (e) {
    loadingFetchingVatTu.value = false;

    await swalErrNotification(e, "Có lỗi xảy ra khi tìm kiếm vật tư");
  }
};

onMounted(async () => {
  setBreadcrumbLinks([
    {
      text: "Định mức cố định",
      routeData: {
        name: "danh-sach-dinh-muc-co-dinh",
      },
    },
    {
      text: "Chi tiết",
    },
  ]);

  await fetchData();

  await fetchVatTuDinhMuc();

  setCurrentPageTitle(
    `${dinhMucDetail.value?.danh_muc_toa_xe.ten_danh_muc} (${dinhMucDetail.value?.cap_sua_chua.ten_cap_sc})`
  );
});

const addVatTu = async ({
  selectingVatTuItems,
}: {
  selectingVatTuItems: IVatTuResponseBase[];
}) => {
  if (isDinhMucHetHan.value) {
    await swalNotification(
      "Không thể thêm vật tư vào định mức cố định đã hết hạn",
      "warning"
    );

    return;
  }

  await DinhMucCoDinhService.attachVatTuItems({
    id_dinh_muc: dinhMucDetail.value?.id ?? 0,
    vat_tu_items: selectingVatTuItems,
  });

  await fetchData();

  await fetchVatTuDinhMuc();
};

const confirmAddVatTuItems = ({
  selectingVatTuItems,
}: {
  selectingVatTuItems: IVatTuResponseBase[];
}) => {
  isAddVatTuModalDisplay.value = false;

  addVatTu({
    selectingVatTuItems: [
      ...selectingVatTuItems,
      ...vatTuDinhMucTransform.value,
    ],
  });
};

const removingVatTuItem = ref<null | IVatTuResponseBase>(null);

const confirmRemoveVatTu = ({ id_vat_tu }) => {
  removingVatTuItem.value =
    vatTuDinhMucTransform.value.find(({ id }) => id === id_vat_tu) ?? null;
};

const removeVatTu = async ({ id_vat_tu }: { id_vat_tu: number }) => {
  if (isDinhMucHetHan.value) {
    await swalNotification(
      "Không thể xóa vật tư khỏi định mức cố định đã hết hạn",
      "warning"
    );
    removingVatTuItem.value = null;

    return;
  }

  const removeIndex = vatTuDinhMucTransform.value.findIndex(
    ({ id }) => id === id_vat_tu
  );

  const selectingVatTuItems = vatTuDinhMucTransform.value.filter(
    (_vatTu, index) => removeIndex !== index
  );

  await addVatTu({
    selectingVatTuItems,
  });

  removingVatTuItem.value = null;
};

watch([currentPage, searchTerm], () => {
  fetchVatTuDinhMuc();
});

watch(
  () => perPage.value,
  async () => {
    await nextTick();

    if (currentPage.value === 1) {
      fetchVatTuDinhMuc();

      return;
    }

    currentPage.value = 1;
  }
);

const searchForVatTuDinhMuc = debounce(() => {
  fetchVatTuDinhMuc();
}, 500);

return (_ctx: any,_cache: any) => {
  const _component_el_input = _resolveComponent("el-input")!

  return (_openBlock(), _createElementBlock(_Fragment, null, [
    (dinhMucDetail.value)
      ? (_openBlock(), _createBlock(AddVatTuItemV2, {
          key: 0,
          "is-modal-open": isAddVatTuModalDisplay.value,
          "show-search-dmvt": false,
          "show-close-button": false,
          "use-for": _unref(AddVatTuItemsFor).ADD_VAT_TU_DINH_MUC_CO_DINH,
          "vat-tu-selected": vatTuDinhMucTransform.value,
          onClose: _cache[0] || (_cache[0] = ($event: any) => (isAddVatTuModalDisplay.value = false)),
          onAdded: confirmAddVatTuItems
        }, null, 8, ["is-modal-open", "use-for", "vat-tu-selected"]))
      : _createCommentVNode("", true),
    _createVNode(ConfirmationDialog, {
      title: "Xác nhận",
      "is-display": !!removingVatTuItem.value,
      size: "sm",
      onOnConfirmButtonClick: _cache[1] || (_cache[1] = ($event: any) => (
      removeVatTu({
        id_vat_tu: removingVatTuItem.value?.id ?? 0,
      })
    )),
      onOnCloseButtonClick: _cache[2] || (_cache[2] = ($event: any) => (removingVatTuItem.value = null)),
      onOnCloseIconClick: _cache[3] || (_cache[3] = ($event: any) => (removingVatTuItem.value = null))
    }, {
      default: _withCtx(() => [
        _createElementVNode("h4", _hoisted_1, [
          _createTextVNode(" Bạn có chắc chắn muốn xoá vật tư "),
          _createElementVNode("span", _hoisted_2, _toDisplayString(removingVatTuItem.value?.ten_vat_tu), 1),
          _createTextVNode(" khỏi định mức cố định không? ")
        ])
      ]),
      _: 1
    }, 8, ["is-display"]),
    _createVNode(PageContentBase, { plain: "" }, {
      default: _withCtx(() => [
        (dinhMucDetail.value)
          ? (_openBlock(), _createBlock(PageContentSection, { key: 0 }, {
              header: _withCtx(() => [
                _createTextVNode("Chi tiết")
              ]),
              actions: _withCtx(() => [
                (showEditButton.value)
                  ? (_openBlock(), _createBlock(Can, {
                      key: 0,
                      all: [_unref(DINH_MUC_CO_DINH).SUA]
                    }, {
                      default: _withCtx(() => [
                        _createVNode(ButtonCustom, {
                          "button-type": _unref(ButtonsType).EDIT,
                          "color-type": _unref(ButtonTypeColors).SUCCESS,
                          "tooltip-message": "Chỉnh sửa đ/mức",
                          onClick: _cache[4] || (_cache[4] = ($event: any) => (_unref(push)(`/quan-ly/dinh-muc-co-dinh/chinh-sua/${_unref(idDinhMuc)}`)))
                        }, null, 8, ["button-type", "color-type"])
                      ]),
                      _: 1
                    }, 8, ["all"]))
                  : _createCommentVNode("", true),
                _createVNode(Can, {
                  all: [_unref(DINH_MUC_CO_DINH).XOA]
                }, {
                  default: _withCtx(() => [
                    _createVNode(ButtonCustom, {
                      "button-type": _unref(ButtonsType).TRASH,
                      "color-type": _unref(ButtonTypeColors).DANGER,
                      "tooltip-message": "Xoá đ/mức",
                      onClick: onDeleteButtonClick
                    }, null, 8, ["button-type", "color-type"])
                  ]),
                  _: 1
                }, 8, ["all"])
              ]),
              illustration: _withCtx(() => [
                _createVNode(DetailTopicLottie, { topic: "dinh-muc" })
              ]),
              default: _withCtx(() => [
                _createVNode(ResponsiveLayout, null, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("ID:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-bolder": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(dinhMucDetail.value.id), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  "col-3": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Số văn bản:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-4": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-bolder": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(dinhMucDetail.value.so_van_ban || "-"), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(ResponsiveLayout, null, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Chi nhánh:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-bolder": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(dinhMucDetail.value.don_vi?.ten_don_vi || "-"), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  "col-3": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Ngày ban hành:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-4": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-bolder": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(formatDisplayingDate)(dinhMucDetail.value.ngay_ban_hanh)), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(ResponsiveLayout, null, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Ngày hiệu lực:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-bolder": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(formatDisplayingDate)(dinhMucDetail.value.ngay_hieu_luc)), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  "col-3": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Ngày hết hạn:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-4": _withCtx(() => [
                    (!dinhMucDetail.value.ngay_het_han)
                      ? (_openBlock(), _createElementBlock("span", _hoisted_3, " Chưa xác định "))
                      : (_openBlock(), _createBlock(LabelCustom, {
                          key: 1,
                          "is-bolder": "",
                          class: _normalizeClass({
                'text-success': !isDinhMucHetHan.value,
                'text-danger': isDinhMucHetHan.value,
              })
                        }, {
                          default: _withCtx(() => [
                            _createTextVNode(_toDisplayString(_unref(formatDisplayingDate)(dinhMucDetail.value.ngay_het_han)), 1)
                          ]),
                          _: 1
                        }, 8, ["class"]))
                  ]),
                  _: 1
                }),
                _createVNode(ResponsiveLayout, null, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Ngày tạo:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-bolder": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(formatDisplayingDate)(
                dinhMucDetail.value.created_at
                  ? new Date(dinhMucDetail.value.created_at)
                  : null
              )), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  "col-3": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Ngày sửa:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-4": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-bolder": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(_unref(formatDisplayingDate)(new Date(dinhMucDetail.value.updated_at))), 1)
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(ResponsiveLayout, null, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Nhóm toa xe:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createVNode(BaseLink, {
                      isOpenInNewTab: "",
                      to: {
              name: 'chi-tiet-danh-muc-toa-xe',
              params: {
                id: dinhMucDetail.value.id_danh_muc_toa_xe,
              },
            }
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(dinhMucDetail.value.danh_muc_toa_xe.ten_danh_muc), 1)
                      ]),
                      _: 1
                    }, 8, ["to"])
                  ]),
                  "col-3": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Cấp sửa chữa:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-4": _withCtx(() => [
                    _createVNode(BaseLink, {
                      isOpenInNewTab: "",
                      to: {
              name: 'chi-tiet-cap-sua-chua',
              params: {
                id: dinhMucDetail.value.id_cap_sc,
              },
            }
                    }, {
                      default: _withCtx(() => [
                        _createTextVNode(_toDisplayString(dinhMucDetail.value.cap_sua_chua.ten_cap_sc), 1)
                      ]),
                      _: 1
                    }, 8, ["to"])
                  ]),
                  _: 1
                }),
                _createVNode(ResponsiveLayout, { "align-items": "align-items-start" }, {
                  "col-1": _withCtx(() => [
                    _createVNode(LabelCustom, { "is-text-muted": "" }, {
                      default: _withCtx(() => [
                        _createTextVNode("Mô tả:")
                      ]),
                      _: 1
                    })
                  ]),
                  "col-2": _withCtx(() => [
                    _createElementVNode("span", _hoisted_4, _toDisplayString(dinhMucDetail.value.mo_ta?.trim() || "-"), 1)
                  ]),
                  "col-3": _withCtx(() => []),
                  "col-4": _withCtx(() => []),
                  _: 1
                })
              ]),
              _: 1
            }))
          : _createCommentVNode("", true),
        (dinhMucDetail.value)
          ? (_openBlock(), _createBlock(PageContentSection, { key: 1 }, {
              header: _withCtx(() => [
                _createTextVNode("Vật tư")
              ]),
              actions: _withCtx(() => [
                (!isDinhMucHetHan.value)
                  ? (_openBlock(), _createBlock(ButtonCustom, {
                      key: 0,
                      "button-type": _unref(ButtonsType).ADD,
                      classes: `rounded-none m-0`,
                      "color-type": _unref(ButtonTypeColors).PRIMARY,
                      "tooltip-message": "Thêm vật tư vào đ/mức",
                      onClick: _cache[5] || (_cache[5] = ($event: any) => (isAddVatTuModalDisplay.value = true))
                    }, null, 8, ["button-type", "color-type"]))
                  : _createCommentVNode("", true)
              ]),
              default: _withCtx(() => [
                _createVNode(FilterContainer, null, {
                  default: _withCtx(() => [
                    _createVNode(FilterItem, { label: "Tìm kiếm" }, {
                      default: _withCtx(() => [
                        _createVNode(_component_el_input, {
                          modelValue: _unref(searchTerm),
                          "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => (_isRef(searchTerm) ? (searchTerm).value = $event : null)),
                          clearable: "",
                          placeholder: "Tìm theo mã hoặc tên ...",
                          onInput: _unref(searchForVatTuDinhMuc)
                        }, null, 8, ["modelValue", "onInput"])
                      ]),
                      _: 1
                    })
                  ]),
                  _: 1
                }),
                _createVNode(DinhMucCoDinhVatTuTable, {
                  "cur-page": _unref(currentPage),
                  "is-loading": _unref(loadingFetchingVatTu),
                  items: vatTuItems.value,
                  "per-page": _unref(perPage),
                  "show-delete-button": !isDinhMucHetHan.value,
                  onRemove: confirmRemoveVatTu
                }, null, 8, ["cur-page", "is-loading", "items", "per-page", "show-delete-button"]),
                (vatTuItems.value.length > 0)
                  ? (_openBlock(), _createBlock(Pagination, {
                      key: 0,
                      page: _unref(currentPage),
                      total: _unref(totalItems),
                      onChangePage: _cache[7] || (_cache[7] = (value) => (currentPage.value = value)),
                      onChangePageSize: _cache[8] || (_cache[8] = (value) => (perPage.value = value))
                    }, null, 8, ["page", "total"]))
                  : _createCommentVNode("", true)
              ]),
              _: 1
            }))
          : _createCommentVNode("", true)
      ]),
      _: 1
    })
  ], 64))
}
}

})